import * as React from 'react';
import cx from 'classnames';
import ReactSelect from 'react-select';

import { FocusEventHandler } from 'types/typescript/eventHandlers';

import './styles/Select.scss';

export type Option = {
  label: string;
  value: string;
};

/**
 * ## A styled [Select](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/select) input built using [React Select](https://github.com/JedWatson/react-select).
 * ### Use a select when you want the user to make exactly one option out of a list of options. Particularly useful for lists with a lot of options (6 or more) or lists with an unknown or variable number of options.
 *
 * ```example
 * <Field>
 *   <Label hidden>Label</Label>
 *   <Select
 *     options={[
 *       {
 *         label: 'Option #1',
 *         value: 'option-1',
 *       },
 *       {
 *         label: 'Option #2',
 *         value: 'option-2',
 *       },
 *       {
 *         label: 'Option #3',
 *         value: 'option-3',
 *       },
 *     ]}
 *   />
 * </Field>
 * ```
 *
 * ```example
 * <Field>
 *   <Label hidden>Label</Label>
 *   <Select
 *     clearable
 *     options={[
 *       { label: 'Option #1', value: 'option-1' },
 *       { label: 'Option #2', value: 'option-2' },
 *       { label: 'Option #3', value: 'option-3' },
 *     ]}
 *     placeholder="Select placeholder"
 *     searchable
 *   />
 * </Field>
 * ```
 *
 *
 * # Events
 *
 * ```example
 * <Field>
 *   <Label hidden>Label</Label>
 *   <Select
 *     options={[
 *       { label: 'Option #1', value: 'option-1' },
 *       { label: 'Option #2', value: 'option-2' },
 *       { label: 'Option #3', value: 'option-3' },
 *     ]}
 *     onChange={() => console.log('onChange')}
 *     onInputKeyUp={() => console.log('onInputKeyUp')}
 *   />
 * </Field>
 * ```
 */

export type Props = {
  autoComplete?: string;
  className?: string;
  clearable?: boolean;
  inputProps?: any;
  name?: string;
  options: Option[];
  placeholder?: string;
  required?: boolean;
  value?: any;
  searchable?: boolean;
  onChange?: Function;
  onInputKeyUp?: Function;
  onFocus?: FocusEventHandler;
  onMenuOpen?: Function;
  disabled?: boolean;
  inline?: boolean;
};

type DefaultProps = {
  clearable: boolean;
  options: Option[];
};

class Select extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    clearable: false,
    options: [],
  };

  _select: ReactSelect | null = null;

  focus = () => {
    if (this._select) {
      this._select.focus();
    }
  };

  handleInputKeyUp = () => {
    const { onInputKeyUp, options } = this.props;

    if (!this._select) {
      return;
    }

    const inputText = this._select.state.inputValue;

    const selected = options.find(option => {
      let optionLabel;
      let inputLabel;

      if (option.label) {
        optionLabel = String(option.label).toLowerCase();
      }

      if (inputText) {
        inputLabel = String(inputText).toLowerCase();
      }

      return inputLabel === optionLabel;
    });

    if (onInputKeyUp) {
      onInputKeyUp(selected);
    }
  };

  handleChange = (option: any) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(option.value);
    }
  };

  render() {
    const {
      autoComplete,
      className,
      clearable,
      inputProps,
      name,
      options,
      placeholder,
      required,
      searchable,
      value,
      onFocus,
      disabled,
      onMenuOpen,
      inline,
    } = this.props;

    for (const option of options) {
      option.value = String(option.value);
    }

    const combinedInputProps = Object.assign({}, inputProps, { onKeyUp: this.handleInputKeyUp });

    return (
      <ReactSelect
        autoComplete={autoComplete}
        clearable={clearable}
        inputProps={combinedInputProps}
        name={name}
        options={options}
        placeholder={placeholder}
        ref={c => {
          this._select = c;
        }}
        required={required}
        searchable={searchable}
        value={value && (String(value) as any)}
        className={cx(className, inline && 'Select--inline')}
        onChange={this.handleChange}
        onFocus={onFocus}
        disabled={disabled}
        onOpen={onMenuOpen}
      />
    );
  }
}

export default Select;
